import React from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft } from 'lucide-react';
import InstallGuide, { GuidePlatform } from './InstallGuide';
import InstallAppButton from '../auth/InstallAppButton';
import EvalModeBanner from '../common/EvalModeBanner';
import { detectPlatform } from '../../services/offline/platform/deviceClass';
import iconBlack from '../../assets/images/logofullblack.png';

/** Public install guide; safe to share with staff (no sign-in, no PHI). */
export const INSTALL_GUIDE_PATH = '/install';

export interface InstallGuidePageProps {
  /** Where "Back" goes: the sign-in page, or the app when signed in. */
  backTo: string;
  backLabel: string;
}

/**
 * /install (Phase 16A): how to install pelora on Android and iPhone / iPad. On a phone or tablet
 * the Install button is offered first; on a computer a QR code opens this page on the phone.
 */
const InstallGuidePage: React.FC<InstallGuidePageProps> = ({ backTo, backLabel }) => {
  const platform = detectPlatform();
  const onComputer = platform === 'desktop';
  const initial: GuidePlatform = platform === 'ios' || platform === 'ipados' ? 'ios' : 'android';
  const guideUrl = typeof window !== 'undefined' ? `${window.location.origin}${INSTALL_GUIDE_PATH}` : INSTALL_GUIDE_PATH;

  return (
    <main className="min-h-screen bg-white px-4 py-6 text-slate-800" style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}>
      <div className="mx-auto w-full max-w-xl">
        <Link to={backTo} className="mb-6 inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-slate-700">
          <ArrowLeft size={18} aria-hidden="true" />{backLabel}
        </Link>
        <img src={iconBlack} alt="Pelora Health" className="mb-6 h-auto w-40 object-contain" />
        <EvalModeBanner />
        <h1 className="mb-2 text-2xl font-bold text-gray-900">Install pelora on your phone or tablet</h1>
        <p className="mb-6 text-base text-gray-600">
          Installing adds a pelora icon to your home screen. It opens like an app, without a browser bar, and lets you keep working where there's no signal.
        </p>

        {onComputer ? (
          <section aria-label="Open on your phone" className="mb-8 flex flex-col items-center gap-4 rounded-xl border border-gray-200 p-5 sm:flex-row sm:items-start">
            <div className="rounded-lg bg-white p-2 ring-1 ring-gray-200">
              <QRCodeSVG value={guideUrl} size={148} aria-label="QR code for the pelora install guide" />
            </div>
            <div className="text-sm text-gray-700">
              <p className="mb-2 text-base font-semibold text-gray-900">Scan with your phone's camera</p>
              <p className="mb-2">It opens this guide on your phone. Install from there: pelora can't be installed for offline use on a computer.</p>
              <p className="break-all text-gray-500">{guideUrl}</p>
            </div>
          </section>
        ) : (
          <div className="mb-8">
            <InstallAppButton variant="login" showGuideLink={false} />
          </div>
        )}

        <InstallGuide initialPlatform={initial} />
      </div>
    </main>
  );
};

export default InstallGuidePage;
